import {
  MapPin,
  Phone,
  MessageCircle,
  Send,
  X,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Crosshair,
  Car,
} from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";

/**
 * Small label+input wrapper used by the dialog. Kept in this file to avoid
 * a one-line extra module while still being reusable within the dialog.
 */
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-display uppercase tracking-widest text-[10px] text-slate-300">
        {label} {required && <span className="text-yellow-400">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SendButtonContent({ state }) {
  if (state === "sending") {
    return (
      <>
        <Loader2 className="h-5 w-5 animate-spin" /> Sending…
      </>
    );
  }
  if (state === "sent") {
    return (
      <>
        <CheckCircle2 className="h-5 w-5" /> Sent to Dispatch
      </>
    );
  }
  return (
    <>
      <Send className="h-5 w-5" strokeWidth={3} /> Send to BM Towing Dispatch
    </>
  );
}

/**
 * ShareLocationDialog — modal shell for the emergency-tow request. Pure
 * presentation: all state and business logic lives in useShareLocationForm.
 */
export default function ShareLocationDialog({ testId, onClose, controller }) {
  const { gps, form, updateField, send, copied, mapsUrl, smsHref, canSend, copyMaps, sendToDispatch } = controller;

  return (
    <div
      data-testid={`${testId}-modal`}
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full sm:max-w-lg bg-slate-950 border-2 border-yellow-400 bm-hard-shadow-yellow my-0 sm:my-8 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between bg-yellow-400 text-slate-900 px-5 py-3 border-b-2 border-black z-10">
          <div className="flex items-center gap-2 font-display font-black uppercase tracking-wider">
            <MapPin className="h-5 w-5" strokeWidth={3} /> Request Emergency Tow
          </div>
          <button
            onClick={onClose}
            data-testid={`${testId}-close`}
            className="p-1 hover:bg-slate-900 hover:text-yellow-400 border-2 border-transparent hover:border-black"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          {/* Big Call Now — always primary */}
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid={`${testId}-call`}
            className="flex items-center justify-center gap-3 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-display font-black uppercase tracking-wider px-5 py-4 border-2 border-black min-h-[60px] bm-hard-shadow"
          >
            <Phone className="h-6 w-6" strokeWidth={3} /> Call {PHONE_DISPLAY}
          </a>

          <div className="text-center text-xs font-display uppercase tracking-widest text-slate-500">
            — or send us your location —
          </div>

          {/* GPS section */}
          <div className="bg-slate-900 border-2 border-slate-700 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-display uppercase tracking-widest text-yellow-400 text-xs">
                Option 1 · Use GPS (fastest)
              </div>
              <button
                type="button"
                onClick={gps.request}
                disabled={gps.loading}
                data-testid={`${testId}-get-gps`}
                className="inline-flex items-center gap-2 bg-slate-950 hover:bg-black text-yellow-400 border-2 border-yellow-400 px-3 py-2 font-display uppercase tracking-widest text-xs disabled:opacity-70"
              >
                {gps.loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Getting GPS…</>
                ) : (
                  <><Crosshair className="h-4 w-4" strokeWidth={3} /> Get my GPS</>
                )}
              </button>
            </div>

            {gps.coords && (
              <div
                data-testid={`${testId}-coords`}
                className="mt-3 text-xs text-slate-200 font-mono break-all"
              >
                <div className="text-yellow-400">GPS locked ✓</div>
                <div>Lat: {gps.coords.lat.toFixed(6)}</div>
                <div>Lng: {gps.coords.lng.toFixed(6)}</div>
                <div className="text-slate-400">Accuracy: ~{Math.round(gps.coords.accuracy)}m</div>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-block text-yellow-400 hover:text-yellow-300 underline underline-offset-4"
                >
                  Open in Google Maps ↗
                </a>
              </div>
            )}

            {gps.error && (
              <div
                data-testid={`${testId}-error`}
                className="mt-3 flex items-start gap-2 border border-red-500 bg-red-500/10 text-red-200 p-3 text-xs"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{gps.error}</span>
              </div>
            )}
          </div>

          {/* Manual address / vehicle / phone */}
          <div className="space-y-3">
            <div className="font-display uppercase tracking-widest text-yellow-400 text-xs">
              Option 2 · Type your details (works everywhere)
            </div>
            <Field label="Where are you?" required={!gps.coords}>
              <input
                type="text"
                value={form.address}
                onChange={(e) => updateField("address", e.target.value)}
                data-testid={`${testId}-address`}
                placeholder="Street address, cross-streets, exit, or landmark"
                className="bm-input"
              />
            </Field>
            <Field label="Vehicle (year / make / model)">
              <input
                type="text"
                value={form.vehicle}
                onChange={(e) => updateField("vehicle", e.target.value)}
                data-testid={`${testId}-vehicle`}
                placeholder="e.g. 2019 Honda Civic"
                className="bm-input"
              />
            </Field>
            <Field label="Your phone (for callback)" required>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                data-testid={`${testId}-phone`}
                placeholder="(425) 555-0123"
                className="bm-input"
              />
            </Field>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              onClick={sendToDispatch}
              disabled={!canSend || send.state === "sending" || send.state === "sent"}
              data-testid={`${testId}-send`}
              className="inline-flex items-center justify-center gap-3 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-display font-black uppercase tracking-wider px-5 py-4 border-2 border-black min-h-[56px]"
            >
              <SendButtonContent state={send.state} />
            </button>

            <div className="grid grid-cols-2 gap-3">
              <a
                href={smsHref}
                data-testid={`${testId}-sms`}
                className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white border-2 border-yellow-400 px-3 py-3 font-display font-black uppercase tracking-wider text-xs min-h-[48px]"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={3} /> Text Us
              </a>
              <button
                type="button"
                onClick={copyMaps}
                disabled={!mapsUrl}
                data-testid={`${testId}-copy`}
                className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/5 disabled:opacity-40 text-slate-300 border-2 border-slate-700 px-3 py-3 font-display uppercase tracking-widest text-xs min-h-[48px]"
              >
                <Copy className="h-4 w-4" />
                {copied ? "Copied" : "Copy Map Link"}
              </button>
            </div>
          </div>

          {send.state === "sent" && (
            <div
              data-testid={`${testId}-send-success`}
              role="status"
              aria-live="polite"
              className="border-2 border-green-500 bg-green-500/10 text-green-200 p-3 text-sm"
            >
              Dispatch received your request. Please also call {PHONE_DISPLAY} for the fastest response.
            </div>
          )}
          {send.state === "error" && (
            <div
              data-testid={`${testId}-send-error`}
              role="alert"
              aria-live="assertive"
              className="border-2 border-red-500 bg-red-500/10 text-red-200 p-3 text-sm"
            >
              {send.error}
            </div>
          )}

          {!canSend && (
            <div
              data-testid={`${testId}-send-hint`}
              className="text-[11px] text-slate-500"
            >
              Fill in your phone plus either GPS or an address to send to dispatch.
            </div>
          )}

          <div className="flex items-center gap-2 border-t border-slate-800 pt-4">
            <Car className="h-4 w-4 text-slate-500 shrink-0" />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              We only use your info to dispatch a driver. Nothing is saved to your device.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
